import { toBrowserLocalISODate } from "common/utils/dates";

import { useCurrentDrawer } from "client/common/hooks/stackables";

import {
  DosageReminder,
  DosageReminderStatus,
  DosageReminderUpdate,
  IsDeliveryMethodSelected,
  ReminderFormValues,
} from "../types";
import { useUpdateDosageReminderMutation } from "../hooks";
import ReminderDrawerForm from "./ReminderDrawerForm";

type ReminderEditFormProps = {
  reminder: DosageReminder;
  canGoBack: boolean;
};

const ReminderEditForm = ({ reminder, canGoBack }: ReminderEditFormProps) => {
  const { close } = useCurrentDrawer();
  const { mutate, isPending, error } = useUpdateDosageReminderMutation(reminder.id, {
    onSuccess: close,
  });

  const getChangedFields = (
    { isActive, nextDoseDate, repeatEvery, intervalUnit, stopDate }: ReminderFormValues,
    isDeliveryMethodSelected: IsDeliveryMethodSelected,
  ): DosageReminderUpdate => {
    if (!isActive) {
      return { status: DosageReminderStatus.Inactive };
    }

    const nextDoseISODate = nextDoseDate && toBrowserLocalISODate(nextDoseDate);
    const stopISODate = stopDate && toBrowserLocalISODate(stopDate);
    const isEmailEnabled = isDeliveryMethodSelected("email");
    const isTextEnabled = isDeliveryMethodSelected("text");
    const isReactivating = !reminder.is_active;

    return {
      ...(isReactivating && { status: DosageReminderStatus.Active }),
      ...(Number(repeatEvery) !== reminder.frequency && {
        frequency: Number(repeatEvery),
      }),
      ...(intervalUnit !== reminder.interval_unit && {
        interval_unit: intervalUnit,
      }),
      ...((isReactivating || nextDoseISODate !== reminder.next_dose_date) && {
        next_dose_date: nextDoseISODate,
      }),
      ...(stopISODate !== (reminder.stop_date ?? null) && {
        stop_date: stopISODate,
      }),
      ...(isEmailEnabled !== reminder.email_enabled && {
        email_enabled: isEmailEnabled,
      }),
      ...(isTextEnabled !== reminder.text_enabled && {
        text_enabled: isTextEnabled,
      }),
    };
  };

  return (
    <ReminderDrawerForm
      patient={reminder.patient}
      item={reminder.item}
      reminder={reminder}
      isSubmitting={isPending}
      submitError={error}
      canGoBack={canGoBack}
      onSubmit={(values, isDeliveryMethodSelected) =>
        mutate(getChangedFields(values, isDeliveryMethodSelected))
      }
    />
  );
};

export default ReminderEditForm;
